import { faTrash, faUserShield } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import type z from "zod";
import { schemas } from "../../api/generated";
import { ActivityDate } from "../ActivityDate";
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type PaginationState,
  type Updater,
  type SortingState,
  type CellContext,
} from "@tanstack/react-table";
import { DataTable } from "../DataTable";
import { useMemo } from "react";

type User = z.infer<typeof schemas.User>;

export type UsersTableProps = {
  data: User[];
  totalUsers: number;
  // eslint-disable-next-line no-unused-vars
  setSelectedUser: (user: User) => void;
  // eslint-disable-next-line no-unused-vars
  setShowConfirmation: (show: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onPromoteUser: (user: User) => void;
  canPromoteUsers: boolean;
  onTableChange: (
    // eslint-disable-next-line no-unused-vars
    pagination: Updater<PaginationState>,
    // eslint-disable-next-line no-unused-vars
    sorting: Updater<SortingState>
  ) => void;
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDescending?: boolean;
};

function DateCell(info: CellContext<User, unknown>) {
  return <ActivityDate date={info.getValue<string>()} />;
}

export function UsersTable({
  data,
  totalUsers,
  setSelectedUser,
  setShowConfirmation,
  onPromoteUser,
  canPromoteUsers,
  onTableChange,
  pageIndex,
  pageSize,
  sortBy,
  sortDescending,
}: UsersTableProps) {
  const tableData = data || [];

  const columns: ColumnDef<User, unknown>[] = useMemo(
    () => [
      {
        id: "first name",
        accessorKey: "firstname",
        meta: { sortKey: "firstname" },
        header: () => "First Name",
        cell: (info) => info.getValue<string>(),
        size: 77,
        minSize: 66,
        maxSize: 120,
      },
      {
        id: "last name",
        accessorKey: "lastname",
        meta: { sortKey: "lastname" },
        header: () => "Last Name",
        cell: (info) => info.getValue<string>(),
        size: 150,
        minSize: 66,
        maxSize: 150,
      },
      {
        id: "email address",
        accessorKey: "email",
        meta: { sortKey: "email" },
        header: () => "Email",
        cell: (info) => info.getValue<string>(),
        size: 180,
        minSize: 180,
        maxSize: 400,
      },
      {
        id: "username",
        accessorKey: "username",
        meta: { sortKey: "username" },
        header: () => "Username",
        cell: (info) => info.getValue<string>(),
        size: 120,
        minSize: 120,
        maxSize: 280,
      },
      {
        id: "role",
        accessorKey: "userType",
        meta: { sortKey: "userType" },
        header: () => "Role",
        cell: (info) => info.getValue<string>(),
        size: 60.3,
      },
      {
        id: "file name",
        accessorFn: (row) => row.files[0],
        meta: { sortKey: "files" },
        header: () => "Assigned Library",
        cell: (info) => {
          const file = info.getValue<string>();
          return (
            <span style={{ maxWidth: "200px" }}>
              <code className="text-body d-block" title={file}>
                {file}
              </code>
            </span>
          );
        },
        minSize: 150,
      },
      {
        id: "registered date",
        accessorKey: "createdAt",
        meta: { sortKey: "createdAt" },
        header: () => (
          <span title="The date and time the user account was created">
            Registered
          </span>
        ),
        cell: DateCell,
        size: 106,
      },
      {
        id: "last login failed date",
        accessorKey: "loginFailedAt",
        meta: { sortKey: "loginFailedAt" },
        header: () => (
          <span title="The date and time when the user made a failed login attempt">
            Login Failed
          </span>
        ),
        cell: DateCell,
        minSize: 106,
        maxSize: 114,
      },
      {
        id: "last login succeeded date",
        accessorKey: "loginSucceededAt",
        meta: { sortKey: "loginSucceededAt" },
        header: () => (
          <span title="The date and time that the user successfully logged in">
            Login Succeeded
          </span>
        ),
        cell: DateCell,
        minSize: 106,
        maxSize: 149,
      },
      {
        id: "last attack library downloaded date",
        accessorKey: "attackLibraryDownloadedAt",
        meta: { sortKey: "attackLibraryDownloadedAt" },
        header: () => (
          <span title="The date and time the attack library was last downloaded">
            Last Download
          </span>
        ),
        cell: DateCell,
        minSize: 106,
        maxSize: 132,
      },
      {
        id: "actions",
        header: () => "Actions",
        enableSorting: false,
        cell: ({ row }) => {
          const user = row.original;

          return (
            <ButtonGroup
              className="d-block mx-auto"
              style={{
                // Work around for built in cell padding
                marginTop: "-6px",
                marginBottom: "-6px",
              }}
            >
              {user.userType === "User" && (
                <OverlayTrigger
                  overlay={
                    <Tooltip id={`promote-${user.id}`}>
                      {canPromoteUsers
                        ? "Promote user to Admin"
                        : "Admin promotion is currently disabled for this environment"}
                    </Tooltip>
                  }
                >
                  {/* span wrapper so the tooltip still shows when the button is disabled */}
                  <span className="d-inline-block">
                    <Button
                      variant="outline-dark"
                      size="sm"
                      aria-label="Promote user to Admin"
                      disabled={!canPromoteUsers}
                      style={
                        canPromoteUsers ? undefined : { pointerEvents: "none" }
                      }
                      onClick={() => onPromoteUser(user)}
                    >
                      <FontAwesomeIcon width={20} icon={faUserShield} />
                    </Button>
                  </span>
                </OverlayTrigger>
              )}
              <Button
                variant="outline-dark"
                size="sm"
                title="Delete user"
                aria-label="Delete user"
                onClick={() => {
                  setSelectedUser(user);
                  setShowConfirmation(true);
                }}
              >
                <FontAwesomeIcon width={20} icon={faTrash} />
              </Button>
            </ButtonGroup>
          );
        },
        meta: {
          width: "min-content",
        },
      },
    ],
    [canPromoteUsers, onPromoteUser, setSelectedUser, setShowConfirmation]
  );

  const table = useReactTable<User>({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: (pagination) => {
      onTableChange(pagination, (prev) => prev);
    },
    onSortingChange: (sorting) => {
      const newSorting =
        typeof sorting !== "function"
          ? sorting
          : sorting(toSorting(sortBy, sortDescending));
      const sortKey = toSortKey(columns, newSorting[0]?.id);
      const modifiedSorting = toSorting(sortKey, newSorting[0]?.desc);
      onTableChange((prev) => ({ ...prev, pageIndex: 0 }), modifiedSorting);
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: totalUsers,
    state: {
      pagination: { pageIndex, pageSize },
      sorting: toSorting(sortBy, sortDescending),
    },
    defaultColumn: {
      size: Number.NaN,
      minSize: Number.NaN,
      maxSize: Number.NaN,
    },
  });

  return <DataTable table={table} itemName="user" />;
}

function toSortKey<T>(columns: ColumnDef<T>[], colId: string) {
  const column = columns.find((col) => col.id === colId);
  if (!column) return colId;
  return column.meta && "sortKey" in column.meta
    ? (column?.meta?.sortKey as string | undefined)
    : colId;
}

function toSorting(sortBy?: string, sortDescending?: boolean) {
  return sortBy
    ? [
        {
          id: sortBy,
          desc: sortDescending || false,
        },
      ]
    : [];
}
